import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { Controller, useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { aboutYouSchema, AboutYouFormValues } from '../rules/aboutYouSchema'
import { LabelInput } from '@/common/ui/LabelInput'
import { Label } from '@/common/ui'
import { Checkbox } from '@/common/ui/checkbox'
import { SearchableAddressForm } from '@/user/address/SearchableAddressForm'
import { DateOfBirthField } from '@/onboarding/shared/components/DateOfBirthField'
import { MobileNumberField } from '@/onboarding/shared/components/MobileNumberField'
import { ONBOARDING_COPY } from '@/locales/en'

export type PageProps = {
    progress?: number
    defaultValues?: AboutYouFormValues
    errors?: Partial<Record<string, string>>
    loading?: boolean
    saving?: boolean
    disabled?: boolean
}

interface ViewModelProps extends PageProps {
    /** Form submission handler — *required for production use*;
        may be omitted in Storybook previews where only form state is displayed. */
    onSubmit?: (data: AboutYouFormValues) => void
}

export function AboutYouForm({
    progress = 45,
    defaultValues,
    errors: externalErrors,
    loading,
    saving,
    disabled,
    onSubmit,
}: ViewModelProps) {
    const {
        register,
        handleSubmit,
        control,
        trigger,
        setValue,
        watch,
        reset,
        setFocus,
        formState: { errors: formErrors, isValid },
    } = useForm<AboutYouFormValues>({
        resolver: yupResolver(aboutYouSchema),
        mode: 'onChange',
        defaultValues,
        shouldUnregister: false,
    })

    useEffect(() => {
        if (defaultValues) {
            reset(defaultValues)
        }
    }, [defaultValues, reset])

    useEffect(() => {
        setFocus('salutation')
    }, [setFocus])

    const countryCode = watch('countryCode', '+61')
    const mobile = watch('mobile', '')
    const differentPostalAddress = watch('differentPostalAddress', false)

    const displayErrors: PageProps['errors'] = externalErrors

    const isDisabled = disabled ?? (!isValid || loading || saving)

    return (
        <Onboarding
            title={ONBOARDING_COPY.aboutYou.title}
            isLoading={saving || loading}
            description={ONBOARDING_COPY.aboutYou.description}
            cta={ONBOARDING_COPY.next}
            onSubmit={onSubmit ? handleSubmit(onSubmit) : () => {}}
            disabled={isDisabled}
            progress={progress}
        >
            <div className="flex flex-col md:flex-row gap-6">
                <LabelInput
                    {...register('salutation')}
                    type="text"
                    id="salutation"
                    required
                    formNoValidate
                    label={ONBOARDING_COPY.aboutYou.form.salutation}
                    placeholder={ONBOARDING_COPY.aboutYou.form.salutationPlaceholder}
                    error={displayErrors?.salutation || formErrors.salutation?.message}
                />
                <LabelInput
                    {...register('firstName')}
                    type="text"
                    id="firstName"
                    required
                    formNoValidate
                    label={ONBOARDING_COPY.aboutYou.form.firstName}
                    placeholder={ONBOARDING_COPY.aboutYou.form.firstNamePlaceholder}
                    error={displayErrors?.firstName || formErrors.firstName?.message}
                />
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                <LabelInput
                    {...register('middleName')}
                    type="text"
                    id="middleName"
                    formNoValidate
                    label={ONBOARDING_COPY.aboutYou.form.middleName}
                    placeholder={ONBOARDING_COPY.aboutYou.form.middleNamePlaceholder}
                    error={displayErrors?.middleName || formErrors.middleName?.message}
                />
                <LabelInput
                    {...register('lastName')}
                    type="text"
                    id="lastName"
                    required
                    formNoValidate
                    label={ONBOARDING_COPY.aboutYou.form.lastName}
                    placeholder={ONBOARDING_COPY.aboutYou.form.lastNamePlaceholder}
                    error={displayErrors?.lastName || formErrors.lastName?.message}
                />
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                <DateOfBirthField control={control} error={displayErrors?.dateOfBirth} formErrors={formErrors} />
                <MobileNumberField
                    control={control}
                    register={register}
                    setValue={setValue}
                    trigger={trigger}
                    countryCode={countryCode}
                    mobile={mobile}
                    mobileError={displayErrors?.mobile}
                    countryCodeError={displayErrors?.countryCode}
                    formErrors={formErrors}
                />
            </div>

            <div>
                <Label>{ONBOARDING_COPY.aboutYou.form.residentialAddress}</Label>
                <SearchableAddressForm
                    type="residential"
                    defaultValues={defaultValues?.residential}
                    register={register as any}
                    setValue={setValue as any}
                    trigger={trigger as any}
                    errors={formErrors?.residential}
                />
                <div className="flex leading-none space-x-3 mt-2 items-center">
                    <Controller
                        name="differentPostalAddress"
                        control={control}
                        render={({ field: { onChange } }) => {
                            return (
                                <Checkbox
                                    id="different-postal-address"
                                    onCheckedChange={onChange}
                                    checked={differentPostalAddress}
                                />
                            )
                        }}
                    />
                    <label
                        htmlFor="different-postal-address"
                        className="text-xs leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                        {ONBOARDING_COPY.aboutYou.form.differentPostalAddress}
                    </label>
                </div>
            </div>

            {differentPostalAddress && (
                <div className="w-full">
                    <Label>{ONBOARDING_COPY.aboutYou.form.postalAddress}</Label>
                    <SearchableAddressForm
                        type="postal"
                        defaultValues={defaultValues?.postal}
                        register={register as any}
                        setValue={setValue as any}
                        trigger={trigger as any}
                        errors={formErrors?.postal}
                    />
                </div>
            )}
        </Onboarding>
    )
}
